/**
 * Post-purchase PO amount / purchasedAmount edits.
 * Reuses the issued-PO 10% approved-amount allowance.
 */

import {
  approvedPurchaseAmountBaseline,
  issuedPurchaseAmountEditKind,
  type IssuedPurchaseAmountEditKind,
} from './purchaseApprovedAmount';
import { roundPurchaseUsd } from './purchaseActualAmount';

export type PurchasedAmountEditKind = IssuedPurchaseAmountEditKind;

export interface PurchaseAmountEditAuditEntry {
  at: Date;
  byId: string;
  byName: string;
  note: string;
  previousAmount: number;
  nextAmount: number;
  previousPurchasedAmount: number | null;
  nextPurchasedAmount: number | null;
  kind: 'within_tolerance' | 'unchanged_fields' | 'authority_confirm';
}

/** Largest dollar figure being set — measured against the approved baseline. */
export function purchasedEditProbeAmount(input: {
  nextAmount: number;
  nextPurchasedAmount: number | null;
}): number {
  const amount = roundPurchaseUsd(Number(input.nextAmount) || 0);
  const purchased =
    input.nextPurchasedAmount == null || !Number.isFinite(input.nextPurchasedAmount)
      ? null
      : roundPurchaseUsd(input.nextPurchasedAmount);
  if (purchased == null) return amount;
  return Math.max(amount, purchased);
}

export function purchasedAmountEditKind(input: {
  approvedAmount?: number | null;
  amount: number;
  nextAmount: number;
  nextPurchasedAmount: number | null;
}): PurchasedAmountEditKind {
  const baseline = approvedPurchaseAmountBaseline({
    approvedAmount: input.approvedAmount,
    amount: input.amount,
  });
  const probe = purchasedEditProbeAmount({
    nextAmount: input.nextAmount,
    nextPurchasedAmount: input.nextPurchasedAmount,
  });
  return issuedPurchaseAmountEditKind(baseline, probe);
}

export function formatPurchasedAmountEditHint(
  baseline: number,
  nextAmount: number,
  nextPurchasedAmount: number | null,
): string {
  const probe = purchasedEditProbeAmount({ nextAmount, nextPurchasedAmount });
  const kind = issuedPurchaseAmountEditKind(baseline, probe);
  if (kind === 'unchanged') {
    return 'Same as the approved amount. Save to update the purchased receipt total only, or change a dollar figure.';
  }
  if (kind === 'within_tolerance') {
    return 'Within 10% of the approved amount. This update applies without new approval.';
  }
  return 'More than 10% off the approved amount — needs authority confirmation (same rule as Adjust amount on an issued PO).';
}
